import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import searchService from '../services/searchService';
import errorHandler from '../utils/errorHandler';

const SearchContext = createContext();

export const useSearch = () => {
    const context = useContext(SearchContext);
    if (!context) {
        throw new Error('useSearch must be used within a SearchProvider');
    }
    return context;
};

export const SearchProvider = ({ children }) => {
    const [searchResults, setSearchResults] = useState({
        vendors: [],
        menu: [],
        total: 0
    });
    const [loading, setLoading] = useState(false);
    const [searchHistory, setSearchHistory] = useState([]);
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [trendingSearches, setTrendingSearches] = useState([]);
    const [popularFilters, setPopularFilters] = useState({});
    const [currentFilters, setCurrentFilters] = useState({
        search: '',
        city_id: '',
        cuisine: '',
        min_order: '',
        max_order: '',
        rating: '',
        price_range: '',
        dietary_options: [],
        features: [],
        sort_by: 'rating',
        sort_order: 'desc'
    });
    const [searchType, setSearchType] = useState('vendors'); // vendors, menu, all
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 12,
        total: 0
    });

    // Initialize search data
    useEffect(() => {
        initializeSearch();
    }, []);

    const initializeSearch = async () => {
        try {
            setLoading(true);
            
            // Load search history
            const history = searchService.getSearchHistory();
            setSearchHistory(history);

            // Load trending searches
            const trending = await searchService.getTrendingSearches();
            setTrendingSearches(trending);

            // Load popular filters
            const filters = await searchService.getPopularFilters();
            setPopularFilters(filters);

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Search initialization' });
        } finally {
            setLoading(false);
        }
    };

    // Perform search
    const search = useCallback(async (query = '', filters = {}, type = 'vendors') => {
        try {
            setLoading(true);
            setSearchType(type);

            const searchParams = {
                ...currentFilters,
                ...filters,
                search: query,
                page: pagination.current,
                per_page: pagination.pageSize
            };

            let resultsObject = { vendors: [], menu: [], total: 0 };
            if (type === 'vendors') {
                const vendorResults = await searchService.searchVendors(searchParams);
                resultsObject = {
                    vendors: vendorResults.data || vendorResults || [],
                    menu: [],
                    total: vendorResults.total || (vendorResults.data ? vendorResults.data.length : (Array.isArray(vendorResults) ? vendorResults.length : 0))
                };
            } else if (type === 'menu') {
                const menuResults = await searchService.searchMenuItems(searchParams);
                resultsObject = {
                    vendors: [],
                    menu: menuResults.data || menuResults || [],
                    total: menuResults.total || (menuResults.data ? menuResults.data.length : (Array.isArray(menuResults) ? menuResults.length : 0))
                };
            } else {
                // Search both
                const [vendorResults, menuResults] = await Promise.all([
                    searchService.searchVendors(searchParams),
                    searchService.searchMenuItems(searchParams)
                ]);
                const vendorsArray = vendorResults.data || vendorResults || [];
                const menuArray = menuResults.data || menuResults || [];
                resultsObject = {
                    vendors: vendorsArray,
                    menu: menuArray,
                    total: (vendorResults.total || vendorsArray.length) + (menuResults.total || menuArray.length)
                };
            }

            setSearchResults(resultsObject);
            setPagination(prev => ({
                ...prev,
                total: resultsObject.total || 0
            }));

            // Save to search history
            if (query.trim()) {
                searchService.saveSearchHistory(query.trim(), type);
                const updatedHistory = searchService.getSearchHistory();
                setSearchHistory(updatedHistory);
            }

            return resultsObject;

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Search execution' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, [currentFilters, pagination.current, pagination.pageSize]);

    // Get search suggestions
    const getSuggestions = useCallback(async (query, type = 'all') => {
        try {
            if (!query || query.length < 2) {
                setSearchSuggestions([]);
                return [];
            }

            const suggestions = await searchService.getSearchSuggestions(query, type);
            setSearchSuggestions(suggestions);
            return suggestions;

        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Get suggestions' });
            return [];
        }
    }, []);

    // Update filters
    const updateFilters = useCallback((newFilters) => {
        setCurrentFilters(prev => ({
            ...prev,
            ...newFilters
        }));
        
        // Reset pagination when filters change
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    // Clear filters
    const clearFilters = useCallback(() => {
        setCurrentFilters({
            search: '',
            city_id: '',
            cuisine: '',
            min_order: '',
            max_order: '',
            rating: '',
            price_range: '',
            dietary_options: [],
            features: [],
            sort_by: 'rating',
            sort_order: 'desc'
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    // Update pagination
    const updatePagination = useCallback((newPagination) => {
        setPagination(prev => ({
            ...prev,
            ...newPagination
        }));
    }, []);

    // Smart search
    const smartSearch = useCallback(async (query, context = {}) => {
        try {
            setLoading(true);
            const results = await searchService.smartSearch(query, context);
            setSearchResults(results);
            return results;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Smart search' });
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    // Voice search
    const voiceSearch = useCallback(async () => {
        try {
            const query = await searchService.voiceSearch();
            await search(query, {}, searchType);
            return query;
        } catch (error) {
            errorHandler.handleApiError(error, { context: 'Voice search' });
            throw error;
        }
    }, [search, searchType]);

    // Clear search
    const clearSearch = useCallback(() => {
        setSearchResults({
            vendors: [],
            menu: [],
            total: 0
        });
        setSearchSuggestions([]);
        setCurrentFilters({
            search: '',
            city_id: '',
            cuisine: '',
            min_order: '',
            max_order: '',
            rating: '',
            price_range: '',
            dietary_options: [],
            features: [],
            sort_by: 'rating',
            sort_order: 'desc'
        });
        setPagination({
            current: 1,
            pageSize: 12,
            total: 0
        });
    }, []);

    // Get search analytics
    const getSearchAnalytics = useCallback(() => {
        return searchService.getSearchAnalytics();
    }, []);

    // Clear search history
    const clearSearchHistory = useCallback(() => {
        searchService.clearSearchHistory();
        setSearchHistory([]);
        message.success('Search history cleared');
    }, []);

    // Get active filters count
    const getActiveFiltersCount = useCallback(() => {
        let count = 0;
        const filters = currentFilters;
        
        if (filters.city_id) count++;
        if (filters.cuisine) count++;
        if (filters.min_order || filters.max_order) count++;
        if (filters.rating) count++;
        if (filters.price_range) count++;
        if (filters.dietary_options?.length > 0) count++;
        if (filters.features?.length > 0) count++;
        
        return count;
    }, [currentFilters]);

    // Get filter summary
    const getFilterSummary = useCallback(() => {
        const activeFilters = [];
        const filters = currentFilters;
        
        if (filters.city_id) {
            const city = popularFilters.cities?.find(c => c.id === filters.city_id);
            if (city) activeFilters.push(`City: ${city.name}`);
        }
        if (filters.cuisine) {
            activeFilters.push(`Cuisine: ${filters.cuisine}`);
        }
        if (filters.min_order || filters.max_order) {
            const min = filters.min_order || '0';
            const max = filters.max_order || '∞';
            activeFilters.push(`Price: ₹${min} - ₹${max}`);
        }
        if (filters.rating) {
            activeFilters.push(`Rating: ${filters.rating}+ stars`);
        }
        if (filters.dietary_options?.length > 0) {
            activeFilters.push(`Dietary: ${filters.dietary_options.join(', ')}`);
        }
        if (filters.features?.length > 0) {
            activeFilters.push(`Features: ${filters.features.join(', ')}`);
        }
        
        return activeFilters;
    }, [currentFilters, popularFilters]);

    const value = {
        // State
        searchResults,
        loading,
        searchHistory,
        searchSuggestions,
        trendingSearches,
        popularFilters,
        currentFilters,
        searchType,
        pagination,
        
        // Actions
        search,
        getSuggestions,
        updateFilters,
        clearFilters,
        updatePagination,
        smartSearch,
        voiceSearch,
        clearSearch,
        getSearchAnalytics,
        clearSearchHistory,
        getActiveFiltersCount,
        getFilterSummary,
        
        // Setters
        setSearchType,
        setCurrentFilters,
        setPagination
    };

    return (
        <SearchContext.Provider value={value}>
            {children}
        </SearchContext.Provider>
    );
};

export default SearchProvider;
